import { makeAutoObservable, reaction, toJS } from "mobx";
import { parseFullName } from "parse-full-name";
import { Userpilot } from "userpilot";
import {
  getFromLocalStorage,
  storeDirectlyIntoLocalStorage,
  removeDirectlyFromLocalStorage,
  LOCAL_STORAGE_USER,
  LOCAL_STORAGE_FORM,
  USER_ID,
  USER,
  EMAIL,
  PHONE,
  CREATED,
  IDP,
  REGISTRATION_FORM,
  UI_STATE,
  LATEST_CAMPAIGN,
  URL_SEARCH_PARAMS,
  getFromStorage,
  MULTI_FACTOR_AUTH,
  STORAGE_TYPE,
  ORIGIN,
  NEW_USER_DATA,
} from "utils/localStorage";
import type { MfaMethod, User } from "graphql/generated";
import { VirtualPlanType } from "graphql/generated";

import type {
  HubspotField,
  HubspotUserProps,
  HubspotFormProps,
  UTM_CAMPAIGNS,
} from "types";
import { ENV } from "utils/hostEnv";
import { FORM_SUBSCRIPTION_IDS, PROD } from "utils/config";
import { getGravityFields, getVarifyFields } from "utils/attribution";
import { http } from "utils/http";
import {
  getGaClientId,
  getGclAwCookie,
  getHubspotUtk,
  matchesRegexps,
} from "utils/utilFunctions";
import { HUBSPOT_ZAPS, ZAPIER_ONBOARDING_ENDPOINT } from "utils/zapier";

type RegistrationForm = Pick<User, "name" | "email">;

const localUserId = getFromLocalStorage(USER_ID, LOCAL_STORAGE_USER, "");
const localUser = getFromLocalStorage(USER, LOCAL_STORAGE_USER, "");
const localEmail = getFromLocalStorage(EMAIL, LOCAL_STORAGE_USER, "");
const localCreated = getFromLocalStorage(CREATED, LOCAL_STORAGE_USER, "");
const localPhone = getFromLocalStorage(PHONE, LOCAL_STORAGE_USER, "");
const localOrigin = getFromLocalStorage(ORIGIN, LOCAL_STORAGE_USER, "");
const localRegistrationForm = getFromLocalStorage<RegistrationForm | null>(
  REGISTRATION_FORM,
  LOCAL_STORAGE_FORM,
  null,
);
const localMfaMethods = getFromLocalStorage<MfaMethod[]>(
  MULTI_FACTOR_AUTH,
  LOCAL_STORAGE_USER,
  [],
);

const getLatestCampaign = (): {} | Record<string, string> => {
  const latestCampaignFromStorage = getFromStorage<string | string[] | null>(
    LATEST_CAMPAIGN,
    URL_SEARCH_PARAMS,
    null,
    STORAGE_TYPE.LOCAL,
  );
  return latestCampaignFromStorage
    ? {
        latest_campaign: Array.isArray(latestCampaignFromStorage)
          ? latestCampaignFromStorage.join(",")
          : latestCampaignFromStorage,
      }
    : {};
};

export class UserStore {
  userOrigin = localOrigin;
  /** @deprecated In components, read `useUser().user?.id`. */
  currentId = localUserId;
  /** @deprecated In components, read `useUser().user?.name`. */
  currentUser = localUser;
  currentPhone = localPhone;
  /** @deprecated In components, read `useUser().user?.email`. */
  currentEmail = localEmail;
  currentCreated = localCreated;
  /** @deprecated In components, read `useUser().user?.managedByIdp`. */
  currentIDP: boolean | null | undefined = undefined;
  registrationForm = localRegistrationForm;
  mfaMethods = localMfaMethods;
  getStartedProgress = 0;

  constructor() {
    makeAutoObservable(this);

    reaction(
      () => this.currentId,
      (id) => {
        storeDirectlyIntoLocalStorage(USER_ID, toJS(id), LOCAL_STORAGE_USER);
      },
    );
    reaction(
      () => this.currentUser,
      (user) =>
        storeDirectlyIntoLocalStorage(USER, toJS(user), LOCAL_STORAGE_USER),
    );
    reaction(
      () => this.currentEmail,
      (email) =>
        storeDirectlyIntoLocalStorage(EMAIL, toJS(email), LOCAL_STORAGE_USER),
    );
    reaction(
      () => this.currentIDP,
      (idp) =>
        storeDirectlyIntoLocalStorage(IDP, toJS(idp), LOCAL_STORAGE_USER),
    );
    reaction(
      () => this.userOrigin,
      (origin) =>
        storeDirectlyIntoLocalStorage(ORIGIN, toJS(origin), LOCAL_STORAGE_USER),
    );
    reaction(
      () => this.currentPhone,
      (phone) =>
        storeDirectlyIntoLocalStorage(PHONE, toJS(phone), LOCAL_STORAGE_USER),
    );
    reaction(
      () => this.currentCreated,
      (created) =>
        storeDirectlyIntoLocalStorage(
          CREATED,
          toJS(created),
          LOCAL_STORAGE_USER,
        ),
    );
    reaction(
      () => this.registrationForm,
      (registrationForm) => {
        if (registrationForm !== null) {
          registrationForm
            ? storeDirectlyIntoLocalStorage(
                REGISTRATION_FORM,
                toJS(registrationForm),
                LOCAL_STORAGE_FORM,
              )
            : removeDirectlyFromLocalStorage(
                REGISTRATION_FORM,
                LOCAL_STORAGE_FORM,
              );
        }
      },
    );
    reaction(
      () => this.mfaMethods,
      (methods) => {
        storeDirectlyIntoLocalStorage(
          MULTI_FACTOR_AUTH,
          toJS(methods),
          LOCAL_STORAGE_USER,
        );
      },
    );
  }

  /** @deprecated In components, read `useUser().hasMfa`. */
  get hasMfa() {
    return this.mfaMethods.length > 0;
  }

  updateName = (name: User["name"]) => {
    this.currentUser = name;
  };
  updateId = (id: User["id"]) => {
    this.currentId = id;
  };
  updateEmail = (email: User["email"]) => {
    this.currentEmail = email;
  };
  updatePhone = (phone: User["phone"]) => {
    this.currentPhone = phone ?? "";
  };
  updateCreated = (created: User["created"]) => {
    this.currentCreated = created ?? "";
  };

  updateIDP = (idp: User["managedByIdp"]) => {
    this.currentIDP = idp;
  };

  updateOrigin = (origin: User["origin"]) => {
    this.userOrigin = origin ?? "";
  };

  setMfaMethods = (mfaMethods: MfaMethod[] = []) => {
    this.mfaMethods = mfaMethods;
  };

  resetMfaMethods = () => {
    this.mfaMethods = [];
  };

  updateGetStartedProgress = (progress: number) => {
    this.getStartedProgress = progress;
  };

  setUser = ({
    name,
    email,
    phone,
    created,
    mfaMethods,
    managedByIdp,
    origin,
  }: Partial<
    Pick<
      User,
      | "name"
      | "email"
      | "phone"
      | "created"
      | "mfaMethods"
      | "managedByIdp"
      | "origin"
    >
  >) => {
    if (name && name !== this.currentUser) this.updateName(name);
    if (email && email !== this.currentEmail) this.updateEmail(email);
    if (phone && phone !== this.currentPhone) this.updatePhone(phone);
    if (created && created !== this.currentCreated) this.updateCreated(created);
    if (managedByIdp && managedByIdp !== this.currentIDP)
      this.updateIDP(managedByIdp);
    if (Array.isArray(mfaMethods) && mfaMethods.length > 0) {
      this.setMfaMethods(mfaMethods);
    }
    if (origin && origin !== this.userOrigin) this.updateOrigin(origin);

    if (name && email) {
      void this.setThirdPartyIdentify({
        id: this.currentId,
        name,
        email,
        created,
      });
    }
  };

  setThirdPartyIdentify = async ({
    id,
    name,
    email,
    created,
  }: Pick<User, "id" | "name" | "email" | "created">) => {
    const latestCampaign = getLatestCampaign();

    Userpilot.identify(id, {
      name,
      email,
      created_at: created,
      ...latestCampaign,
    });
  };

  setRegistrationForm = ({ name, email }: Pick<User, "name" | "email">) => {
    this.registrationForm = { name, email };
  };

  clearRegistrationForm = () => {
    this.registrationForm = null;
  };

  clearStore = () => {
    this.currentUser = "";
    // currentUserImg = localAvatar;
    this.currentPhone = "";
    this.currentEmail = "";
    this.currentId = "";
    this.currentIDP = undefined;
    this.registrationForm = null;
    storeDirectlyIntoLocalStorage(UI_STATE, {}, LOCAL_STORAGE_USER);
  };

  forgetUser = () => {
    this.currentUser = "";
    this.currentId = "";
  };

  /**
   * Sends a request to a Zapier webhook,
   * which then submits a Hubspot form
   * using the zap's Hubspot integration.
   */
  sendToHubspotZap = async (
    zapUrl: (typeof HUBSPOT_ZAPS)[keyof typeof HUBSPOT_ZAPS],
    variables?: Record<string, string | boolean | number | Record<string, any>>,
    projectId?: string,
  ) => {
    try {
      await this.postZap({
        zapUrl,
        /**
         * Default value is a blank space string rather
         * than an empty one because projectId is a required
         * field for postZap requests.
         */
        projectId: projectId || " ",
        email: this.currentEmail,
        userId: this.currentId || "",
        ...variables,
      });
    } catch (e) {
      console.error(`There was en error sending data to Zapier: ${e}`);
    }
  };

  sendNewUserToHubspot = async ({
    id,
    email,
    name,
    isVerified = false,
    submissionPage = "",
  }: HubspotFormProps) => {
    const newUserData: HubspotUserProps = JSON.parse(
      localStorage.getItem(NEW_USER_DATA) || "{}",
    );
    const { utm_params, is_invited, wants_subscription } = newUserData;
    const { first = "", last = "" } = parseFullName(name);
    const gclid = getGclAwCookie();

    await this.sendToHubspotZap(HUBSPOT_ZAPS.CLOUD_NEW_USER_LEAD, {
      source: "cloud-v2-console",
      hubspotUtk: getHubspotUtk() || "",
      timescale_user_id: id,
      email,
      name,
      firstname: first,
      lastname: last,
      created: this.currentCreated,
      environment: ENV.NAME === PROD ? "production" : "development",
      // Return an empty string if this is false. Because this field
      // is a checkbox in Hubspot which accepts "Yes" or "No" (and not
      // a boolean, weird, I know), and a blank value just excludes the field.
      cloud_trial_created_from_invite: is_invited ? "Yes" : "",
      is_verified_user: is_invited || isVerified,
      utm_campaign: utm_params?.utm_campaign || "",
      utm_content: utm_params?.utm_content || "",
      utm_keyword: utm_params?.utm_keyword || "",
      utm_medium: utm_params?.utm_medium || "",
      utm_source: utm_params?.utm_source || "",
      utm_term: utm_params?.utm_term || "",
      ga_client_id: getGaClientId() || "",
      ...(gclid && { gclid }),
      // Blank for signups initiated in this app. IDPLanding passes a real
      // value through for signups initiated from the other project that
      // shares this IDP/Google SSO flow.
      submission_page: submissionPage,
      // Varify A/B attribution (null when none, so the spread adds nothing).
      ...getVarifyFields(),
      // Gravity ad-click attribution (null when none) — see getGravityFields.
      ...getGravityFields(),
      ...(wants_subscription && {
        gdpr: [
          {
            id: FORM_SUBSCRIPTION_IDS["forge"],
            value: wants_subscription,
          },
        ],
      }),
    });
  };

  updateHubspotContactProperties = async (
    projectId: string | undefined,
    properties: Record<string, any>,
  ) => {
    await this.sendToHubspotZap(
      HUBSPOT_ZAPS.CLOUD_UPDATE_HUBSPOT_PROPERTIES,
      {
        ...properties,
      },
      projectId,
    );
  };

  updateHubspotContactProjectId = async (projectId: string) => {
    if (
      // Block CI reporting
      matchesRegexps(this.currentEmail || "", [
        /timescale-ci\+.+@timescale\.com/,
        /test-forge-integration.+@timescale\.com/,
      ])
    ) {
      return;
    } else {
      try {
        await userStore.submitHubspotForm("project", [
          { name: "email", value: userStore.currentEmail },
          {
            name: "project_id",
            value: projectId,
          },
        ]);
      } catch {
        console.log("Error updating contact project ID");
      }
    }
  };

  /**
   * The Plan Type Hubspot form (at the /onboarding_plan
   * route of web-endpoint).
   *
   * This form is submitted after a plan is selected in
   * the plan selection screen in the onboarding flow
   * (after new user email verification).
   */
  updateHubspotContactOnboardingPlanSelection = async (
    planType: VirtualPlanType,
  ) => {
    if (
      // Block CI reporting
      matchesRegexps(this.currentEmail || "", [
        /timescale-ci\+.+@timescale\.com/,
        /test-forge-integration.+@timescale\.com/,
      ])
    ) {
      return;
    } else {
      try {
        const planTypeMap: Record<VirtualPlanType, string> = {
          [VirtualPlanType.Unspecified]: "Unspecified",
          [VirtualPlanType.AwaitingPlan]: "Awaiting",
          [VirtualPlanType.Free]: "Free",
          [VirtualPlanType.Performance]: "Performance",
          [VirtualPlanType.Scale]: "Scale",
          [VirtualPlanType.Enterprise]: "Enterprise",
          [VirtualPlanType.Ghost]: "Ghost",
        };

        await userStore.submitHubspotForm("onboarding_plan", [
          { name: "email", value: userStore.currentEmail },
          {
            name: "plan_type",
            value: planTypeMap[planType],
          },
        ]);
      } catch {
        console.log("Error updating contact onboarding plan selection");
      }
    }
  };

  sendSupportRequestToHubspot = async (content: HubspotField[]) => {
    await this.submitHubspotForm("plan", content);
  };

  /**
   * Sends a request to web-endpoint's /v2/ route,
   * (responses.timescale.com/v2/ or dev-responses.timescale.com/v2/
   * depending on the environment) to submit Hubspot forms.
   *
   * The `endpoint` arg should match the HUBSPOT_FORM_IDS
   * property key of the form that needs to be submitted, defined here:
   * https://github.com/timescale/web-endpoint/blob/main/constants.js
   *
   * You can copy the ID of the corresponding form and paste it in
   * the search box of the Hubspot Forms page to view the form and
   * its submissions: https://app.hubspot.com/forms/3409477
   */
  submitHubspotForm = async (endpoint: string, variables: HubspotField[]) => {
    const hubspotUtk = getHubspotUtk();

    try {
      return await http.fetch(`${ENV.HUBSPOT}${endpoint}`, {
        method: "POST",
        json: [
          ...variables,
          ...(hubspotUtk ? [{ name: "hutk", value: hubspotUtk }] : []),
        ],
        keepalive: true,
      });
    } catch {
      return { status: 500 };
    }
  };

  postZap = async (
    variables: Record<string, string | number | boolean | Record<string, any>>,
  ) => {
    try {
      const data = await http.json<{ errors?: unknown }>(
        `${ENV.WEB_ENDPOINT}/${ZAPIER_ONBOARDING_ENDPOINT}`,
        {
          method: "POST",
          json: variables,
          keepalive: true,
        },
      );
      if (!data || data?.errors) {
        throw new Error(
          "Error posting to Zapier. zapUrl: " + variables?.zapUrl,
        );
      }
    } catch {
      throw new Error("Error posting to Zapier. zapUrl: " + variables?.zapUrl);
    }
  };

  isUtmCampaign = ({ campaignName }: { campaignName: UTM_CAMPAIGNS }) => {
    const campaign =
      JSON.parse(localStorage?.getItem(NEW_USER_DATA) || "{}").utm_params || {};
    return campaignName && campaign?.utm_campaign === campaignName;
  };
}

export const userStore = new UserStore();
